import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsFetching, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import * as amplitude from '@amplitude/analytics-browser';

import {
  Button,
  SkeletonLoader,
} from '@unified-checkout/design-system';

import { REACH_PAGE_EVENTS } from '@app/dashboard/constants/tracking_events/reach';
import { useLangStore } from '@app/dashboard/store/lang';
import { fetchCurrentPlan } from '@app/dashboard/services/inner_layout/reach/bundles';
import { fetchSenderName } from '@app/dashboard/services/inner_layout/reach/campaigns';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import {
  SYSTEM_EVENTS_GROUPING_CODES,
  SYSTEM_EVENTS_SRC_FEATURES,
} from '@app/dashboard/constants/tracking_events/system';
import { onToasterShowHandler } from '@app/dashboard/utils/tracking/onToasterShowHandler';
import { notify } from '@app/dashboard/utils/notifier';

import CreateCampaign from './CreateCampaign';

import styles from './header.module.scss';

const Header = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const isFetching = useIsFetching();
  const { t } = useTranslation();
  const { lang } = useLangStore();

  const { data: currentPlanData = {}, isError: isCurrentPlanError } = useQuery({
    queryKey: [QUERY_KEYS.REACH_GET_CURRENT_PLAN],
    queryFn: fetchCurrentPlan,
    select: (data) => data.data?.data,
  });

  const { data: senderNameData = {}, isError: isSenderNameError } = useQuery({
    queryKey: [QUERY_KEYS.REACH_GET_SENDER_NAME],
    queryFn: fetchSenderName,
    select: (data) => data.data?.data,
  });

  const { last_sms_subscibed_plans_history, available_amount, used_amount } =
    currentPlanData || {};
  const isBundleExpired = !!last_sms_subscibed_plans_history
    ? last_sms_subscibed_plans_history?.expired
    : true;

  const senderName = senderNameData?.sender_name || '';

  const handleNavigateToBundles = () => {
    navigate('/reach/bundles');
    amplitude.track(REACH_PAGE_EVENTS.CAMPAIGNS_PAGE.CLICK_UPGRADE_FROM_MAIN);
  };

  const handleCreateCampaignClick = () => {
    setIsModalOpen(true);
    amplitude.track(REACH_PAGE_EVENTS.CAMPAIGNS_PAGE.CLICK_CREATE_CAMPAIGN);
  };

  useEffect(() => {
    if (isSenderNameError) {
      const errMessage = t(
        'innerLayout.reach.campaigns.header.fetchSenderNameError'
      );
      notify.error(errMessage, {
        timeout: 4,
        onShowHandler: () =>
          onToasterShowHandler({
            type: 'error',
            trackingCode:
              SYSTEM_EVENTS_GROUPING_CODES.REACH.CAMPAIGNS.SENDER_NAME,
            msg: errMessage,
            srcFeature: SYSTEM_EVENTS_SRC_FEATURES.REACH.CAMPAIGNS,
          }),
      });
    }
  }, [isSenderNameError]);

  return (
    <header className={styles['campaigns__header']}>
      <section className={styles['campaigns__header-content']}>
        <h1 className={styles['campaigns__header-content-title']}>
          {t('innerLayout.reach.campaigns.header.heading')}
        </h1>
        <Button
          disabled={
            !!isFetching || isBundleExpired || isCurrentPlanError || !senderName
          }
          text={t('innerLayout.reach.campaigns.header.createCampaign')}
          onClickHandler={handleCreateCampaignClick}
          size="md"
        />
      </section>
      
      <CreateCampaign
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        senderName={senderName}
        availableAmount={available_amount}
        usedAmount={used_amount}
      />

      <section className={styles['campaigns__header-sub-content']}>
        <h2 className={styles['campaigns__header-sub-content-title']}>
          {t('innerLayout.reach.campaigns.header.title')}
        </h2>
        <p className={styles['campaigns__header-sub-content-subtitle']}>
          {t('innerLayout.reach.campaigns.header.subTitle')}
        </p>
      </section>
      {!!isFetching ? (
        <SkeletonLoader noOfRows={2} height="80px" type="transparent_box" />
      ) : (
        <div className={styles['campaigns__header-active-plan']}>
          <p className={styles['campaigns__header-active-plan-title']}>
            {t(
              `innerLayout.reach.campaigns.header.${
                isBundleExpired ? 'subscribeToNewBundle' : 'currentActivePlan'
              }`,
              {
                plan:
                  lang === 'en'
                    ? last_sms_subscibed_plans_history?.plan?.name_en
                    : last_sms_subscibed_plans_history?.plan?.name_ar,
              }
            )}
          </p>
          <div className={styles['campaigns__header-active-plan-info']}>
            <p className={styles['campaigns__header-active-plan-info-title']}>
              {t(
                `innerLayout.reach.campaigns.header.${
                  isBundleExpired ? 'noAvailableSMS' : 'availableSMS'
                }`,
                {
                  available: available_amount,
                  total: used_amount + available_amount,
                }
              )}
            </p>
            <Button
              text={t('innerLayout.reach.campaigns.header.upgrade')}
              variant="link"
              onClickHandler={handleNavigateToBundles}
            />
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
